import React, { createContext, useContext, useState, useEffect } from 'react'

interface AuthContextType {
  isAuthenticated: boolean
  apiKey: string | null
  login: (apiKey: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [apiKey, setApiKey] = useState<string | null>(null)

  useEffect(() => {
    // Check for stored API key on mount
    const storedApiKey = localStorage.getItem('heroku_api_key')
    if (storedApiKey) {
      setApiKey(storedApiKey)
      setIsAuthenticated(true)
    }
  }, [])

  const login = async (apiKey: string) => {
    try {
      // Validate API key with Heroku
      const response = await fetch('https://api.heroku.com/account', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'application/vnd.heroku+json; version=3',
        },
      })

      if (!response.ok) {
        throw new Error('Invalid API key')
      }

      // Store API key and update state
      localStorage.setItem('heroku_api_key', apiKey)
      setApiKey(apiKey)
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('heroku_api_key')
    setApiKey(null)
    setIsAuthenticated(false)
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, apiKey, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
} 